"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapv8TypeaheadCategoriesToV9 = exports.transposeConfig = void 0;
const constants_1 = require("../../constants");
const defaultConfig_1 = require("../../defaultConfig");
// maps the search V8 typeahead categories to the search V9 categories
const v8tov9TypeaheadCategoryMap = {
    clinical_combined: 'clinical_keywords',
    clinical_experience: 'clinical_keywords',
    name: 'provider_name',
    network_affiliation: 'network_affiliation',
    practice_group: 'practice_group',
    practice_location: 'location_name',
    primary_care: 'primary_care',
    primary_care_condition: 'primary_care',
    provider_name: 'provider_name',
    specialty: 'specialties',
    specialty_strict: 'specialties',
    specialty_synonym: 'specialties',
    // might get v9 categories from legacy configs too (part of migration), so handle it
    clinical_keywords: 'clinical_keywords',
    conditions_symptoms: 'conditions_symptoms',
    location_name: 'location_name',
    marketable_locations: 'marketable_locations',
    marketable_location_type: 'marketable_location_type',
    marketable_location_services: 'marketable_location_services',
    specialties: 'specialties',
    treatments_procedures: 'treatments_procedures'
};
/**
 * Transposes a legacy CS config to the new config shape the search widget expects. This
 * is a straight tranpose; it does not translate categories, filters, etc. to search v9.
 *
 * NOTE: this function assumes it's working with a fully fleshed-out V8Server config (e.g. the
 * result of calling buildConfigFromPartialConfigV8 against a config passed from CS first)
 * @param v8Config V8ServerConfig
 * @returns ServerConfig
 */
const transposeConfig = (v8Config) => {
    const transposedParams = {
        allow_empty_search: v8Config.params.allow_empty_search,
        focus_on_widget: v8Config.params.focus_on_widget,
        persist_query: v8Config.params.persist_query,
        show_location: v8Config.params.show_location,
        location_radius: v8Config.params.location_search_radius,
        sort_order: v8Config.params.sort_order.split(','),
        typeahead_categories: tranposeTypeaheadCategories(v8Config.params.typeahead_categories, v8Config.display_dictionary),
        template_name: 'template1',
        open_results_in_new_tab: false,
        use_specialty_name_in_typeahead: v8Config.params.use_specialty_name_in_typeahead || false,
        // group_suggestions_by_category defaults to false
        group_suggestions_by_category: v8Config.params.group_suggestions_by_category === true
    };
    // filters are not required, so only add them if they are defined in the v8 config
    if (v8Config.params.pre_filters)
        transposedParams.filters = v8Config.params.pre_filters;
    if (v8Config.params.external_link) {
        transposedParams.external_link = v8Config.params.external_link.link;
    }
    const transposedDisplayDict = buildDisplayDictFromV8DisplayDict(v8Config.display_dictionary);
    return Object.assign(Object.assign({}, v8Config), { params: Object.assign({}, transposedParams), display_dictionary: Object.assign({}, transposedDisplayDict), add_ons: {} });
};
exports.transposeConfig = transposeConfig;
/**
 * Tranposes the old typeahead categories from the CS config to the new shape, which is
 * pretty much the same, but we changed display_as to display_text and backfill that
 * text either from the application_string_templates or defaults defined in constants.
 * @param categories V8TypeaheadCategory[] the legacy categories from the CS config
 * @param v8DisplayDict V8DisplayDictionary the application_string_templates from the CS config
 * @returns TypeaheadCategory[]
 */
const tranposeTypeaheadCategories = (categories, v8DisplayDict) => {
    return categories.map((category) => ({
        category: category.category,
        display_text: category.display_as ||
            v8DisplayDict[`search.searchtypes.${category.category}`] ||
            constants_1.DEFAULT_TYPEAHEAD_DISPLAY_TEXT[category.category]
    }));
};
// NOTE:
// if the key exists, but has an empty value,
// we'll want to hide the label instead
const parseLabel = (key, dict, defaultLabel) => {
    // exists but is null/empty string
    if (key in dict && !dict[key]) {
        return '';
    }
    return dict[key] || defaultLabel;
};
/**
 * Builds a display dict for labels, placeholder text, etc. from the application
 * string templates that come back from CS
 * @param v8DisplayDict V8DisplayDictionary
 * @returns DisplayDictionary
 */
function buildDisplayDictFromV8DisplayDict(v8DisplayDict) {
    const defaultDisplayDict = (0, defaultConfig_1.defaultConfig)().display_dictionary;
    return {
        'button.label': parseLabel('searchwidget.buttontext', v8DisplayDict, defaultDisplayDict['button.label']),
        'button.icon': parseLabel('searchwidget.buttonicon', v8DisplayDict, defaultDisplayDict['button.icon']),
        'button.aria_label': parseLabel('searchwidget.button.aria_label', v8DisplayDict, defaultDisplayDict['button.aria_label']),
        'free_text.label': parseLabel('searchwidget.label', v8DisplayDict, defaultDisplayDict['free_text.label']),
        'free_text.empty_search_error': parseLabel('free_text.empty_search_error', v8DisplayDict, defaultDisplayDict['free_text.empty_search_error']),
        'free_text.placeholder': v8DisplayDict['searchwidget.placeholder'] ||
            defaultDisplayDict['free_text.placeholder'],
        'free_text.no_suggestions': v8DisplayDict['searchwidget.nosuggestions'] ||
            defaultDisplayDict['free_text.no_suggestions'],
        'free_text.aria_label': v8DisplayDict['searchwidget.arialabel'] ||
            defaultDisplayDict['free_text.aria_label'],
        'free_text.aria_label_with_suggestions': v8DisplayDict['searchwidget.arialabelwithsuggestions'] ||
            defaultDisplayDict['free_text.aria_label_with_suggestions'],
        'locale.label': parseLabel('searchwidget.locationlabel', v8DisplayDict, defaultDisplayDict['locale.label']),
        'locale.placeholder': v8DisplayDict['searchwidget.locationplaceholder'] ||
            defaultDisplayDict['locale.placeholder'],
        // TODO remove external_link when we build add-ons
        'external_link.pre_link_text': v8DisplayDict['searchwidget.externallinkpretext'] || '',
        'external_link.link_text': v8DisplayDict['searchwidget.externallink'] ||
            defaultDisplayDict['external_link.link_text']
    };
}
/**
 * Transposes the typeahead categories defined in the CS config from the
 * v8 version to the new version for search widget v2. The display_text
 * will come either from the config display_as (v8), the application string
 * templates or the default defined in the code
 * @param v8Categories V8TypeaheadCategory[]
 * @param v8DisplayDict V8DisplayDictionary
 * @returns TypeaheadCategory[]
 */
function mapv8TypeaheadCategoriesToV9(v8Categories, v8DisplayDict) {
    const result = [];
    v8Categories.forEach((v8Category) => {
        const categoryTextKey = (`search.searchtypes.${v8Category.category}`);
        const displayTextFromAppStringTemplates = v8DisplayDict[categoryTextKey];
        result.push({
            // #KENG-29148
            category: v8tov9TypeaheadCategoryMap[v8Category.category],
            display_text: 
            // category has already been transposed to the new shape, so look in display_text, NOT display_as
            v8Category.display_text ||
                displayTextFromAppStringTemplates ||
                constants_1.DEFAULT_TYPEAHEAD_DISPLAY_TEXT[v8tov9TypeaheadCategoryMap[v8Category.category]]
        });
    });
    return result;
}
exports.mapv8TypeaheadCategoriesToV9 = mapv8TypeaheadCategoriesToV9;
//# sourceMappingURL=mapV8ConfigToV9.js.map